/**
 * Paso Audiencia del asistente, pestaña «Del Público de Activa» (HATSU, 16/10/2026): las personas del Público con
 * WhatsApp, filtradas por segmento y actividad (los mismos filtros de la pantalla Público).
 *
 * Por WhatsApp oficial sólo sale quien dijo que sí, con el celular con el que lo dijo (el backend,
 * `audiencia-publico.util`): el sí de la Base del oficial o, desde el 17/10/2026, el de la base de ventas (el del
 * archivo o el que declaró el comercio). Lo que viene de Snov o de un análisis no vale. La pestaña lo dice ANTES, con
 * la misma cuenta que el chequeo (`GET /audiencias/publico`): si no, la campaña recién fallaba al confirmar con «no
 * hay a quién mandarle», después de mapear y probar.
 *
 * Con una lista de un archivo («Subir un Excel», 27/10/2026), el filtro es además «sólo los del archivo»: se dice cuál
 * y cuántos de su gente están en el Público, y se puede sacar para volver a todo el Público.
 *
 * Con el filtro «Por compras» (28/10/2026, `compras`), cuenta por `GET /audiencias/compras/publico`: los que saca el
 * filtro van primero entre los que quedan afuera, y el bloque (`porCompras`) dice cuántos quedan. Con exclusiones
 * (`excluir`, 28/10/2026), por el mismo: sus motivos van después del filtro, y los bloques reciben cuántos saca cada cosa.
 *
 * Lo que cuenta lo pasa a «Tu campaña» (`onContado`, 29/10/2026): el panel muestra lo elegido con la misma cuenta.
 */

import { useEffect, useId, useRef, useState, type ReactNode } from "react";
import { Link } from "react-router-dom";
import { AlertTriangle, FileSpreadsheet, Loader2, X } from "lucide-react";
import type { AlcancePublico, ExcluirAudiencia, FiltroAudienciaPublico, FiltroCompras, ListaPublico, ResumenCompras, ResumenExclusiones } from "../types";
import { oficialApi } from "../api";
import { leerError, leerRespuesta, textoError } from "../logic/respuesta";
import { MOTIVO_EXCLUSION, textoYaRecibio } from "../logic/textos";
import { TEXTO_ACTIVIDAD } from "../../publico/logic/publico";
import { num, plural } from "../../campana-nueva/utils/format";
import { RUTA_DECLARAR_CONSENTIMIENTO } from "../logic/alcance";
import { R_CARGAS } from "./base/comun";

export const FILTRO_PUBLICO_VACIO: FiltroAudienciaPublico = { segmento: "", actividad: "" };

/** Lo que contó la pestaña, para «Tu campaña»: cuántos pueden (null mientras cuenta o si no pudo), los segmentos y el archivo. */
export type ContadoPublico = { pueden: number | null; segmentos: { id: string; nombre: string }[]; archivo: string | null };

/** Las actividades que sirven para mandar: «pidió la baja» no (el backend lo rechaza: el chequeo los saca a todos). */
export const ACTIVIDADES_AUDIENCIA = (Object.keys(TEXTO_ACTIVIDAD) as (keyof typeof TEXTO_ACTIVIDAD)[]).filter((a) => a !== "baja");


/** Los motivos por los que quedan afuera, en palabras, sin «sin consentimiento» (va aparte, con su explicación). */
export function otrosExcluidos(a: Pick<AlcancePublico, "excluidos" | "fatiga">): string[] {
  const out: string[] = [];
  for (const [m, n] of Object.entries(a.excluidos || {})) {
    if (m === "sin_optin" || !n) continue;
    const titulo = (MOTIVO_EXCLUSION as Record<string, { titulo: string } | undefined>)[m]?.titulo || m;
    out.push(`${num(n)} · ${titulo}`);
  }
  // la regla de la marca: K mensajes cada N días, sumando el oficial y los WhatsApp por QR
  if (a.fatiga?.excluidos) out.push(`${num(a.fatiga.excluidos)} ${textoYaRecibio(a.fatiga.excluidos, a.fatiga.dias, a.fatiga.maximo ?? 1)}`);
  return out;
}

/** Lo que se dice de los que no tienen un sí que valga: cuántos, y cuántos de ellos dijeron que sí en un análisis. */
export function textoSinConsentimiento(a: Pick<AlcancePublico, "excluidos" | "siFueraDeLaBase">): string {
  const sin = Number(a.excluidos?.sin_optin) || 0;
  if (!sin) return "";
  const base = `${num(sin)} no ${plural(sin, "tiene", "tienen")} un sí para WhatsApp oficial`;
  if (!a.siFueraDeLaBase) return `${base}.`;
  return `${base}: ${num(a.siFueraDeLaBase)} de ${plural(a.siFueraDeLaBase, "ellos dijo", "ellos dijeron")} que sí en un análisis de ventas por QR, y ese sí no dice cómo ni cuándo.`;
}

interface Props {
  filtro: FiltroAudienciaPublico;
  onFiltro: (f: FiltroAudienciaPublico) => void;
  /** la lista del filtro si ya se tiene (recién subida); si no, se pide por su id */
  lista?: ListaPublico | null;
  /** el filtro «Por compras» con el que se cuenta (null = sin filtro) */
  compras?: FiltroCompras | null;
  /** las exclusiones con las que se cuenta (null = sin exclusiones) */
  excluir?: ExcluirAudiencia | null;
  /** los bloques «Por compras» y «Excluir», con lo que dijo el conteo de cada uno */
  porCompras?: (r: { resumen: ResumenCompras | null; exclusiones: ResumenExclusiones | null; contando: boolean }) => ReactNode;
  /** lo que contó, para «Tu campaña» */
  onContado?: (c: ContadoPublico) => void;
}

export function AudienciaPublico({ filtro, onFiltro, lista: listaDada = null, compras = null, excluir = null, porCompras, onContado }: Props) {
  const idSeg = useId();
  const idAct = useId();
  const [alcance, setAlcance] = useState<AlcancePublico | null>(null);
  /** Los segmentos: los de la primera respuesta, para que el selector no se vacíe mientras se recuenta. */
  const [segmentos, setSegmentos] = useState<AlcancePublico["publico"]["segmentos"]>([]);
  const [contando, setContando] = useState(false);
  const [error, setError] = useState("");
  const [intento, setIntento] = useState(0);
  const seq = useRef(0);
  /** la lista del archivo del filtro, pedida por su id cuando no vino (una campaña que se vuelve a abrir) */
  const [listaLeida, setListaLeida] = useState<ListaPublico | null>(null);
  const [errorLista, setErrorLista] = useState("");
  const lista = filtro.lista ? (listaDada?.id === filtro.lista ? listaDada : listaLeida?.id === filtro.lista ? listaLeida : null) : null;

  useEffect(() => {
    if (!filtro.lista || listaDada?.id === filtro.lista) return;
    let vivo = true;
    setErrorLista("");
    void (async () => {
      try {
        const l = leerRespuesta<ListaPublico>(await oficialApi.listaPublico(filtro.lista!));
        if (!vivo) return;
        if (l.ok && l.data) setListaLeida(l.data);
        else setErrorLista(l.ok ? "El servidor no devolvió la lista." : l.error || textoError(l));
      } catch (e) {
        if (vivo) setErrorLista(textoError(leerError(e)));
      }
    })();
    return () => { vivo = false; };
  }, [filtro.lista, listaDada?.id]);
  /** el filtro sin la lista: vuelve a todo el Público, con el segmento y la actividad elegidos */
  const sinLista = (): FiltroAudienciaPublico => ({ segmento: filtro.segmento, actividad: filtro.actividad });

  // el filtro de compras y las exclusiones como texto: un objeto nuevo con lo mismo no vuelve a contar
  const claveCompras = compras ? JSON.stringify(compras) : "";
  const claveExcluir = excluir ? JSON.stringify(excluir) : "";
  // Cuenta con el filtro de ahora; una respuesta vieja (se cambió el filtro mientras contaba) se descarta.
  useEffect(() => {
    const mio = ++seq.current;
    setContando(true);
    setError("");
    const t = setTimeout(() => {
      void (async () => {
        try {
          const conCompras: FiltroCompras | null = claveCompras ? JSON.parse(claveCompras) : null;
          const conExcluir: ExcluirAudiencia | null = claveExcluir ? JSON.parse(claveExcluir) : null;
          const l = leerRespuesta<AlcancePublico>(await (conCompras || conExcluir
            ? oficialApi.audienciaPublicoConCompras(filtro, conCompras, conExcluir)
            : oficialApi.audienciaPublico(filtro)));
          if (mio !== seq.current) return;
          if (!l.ok || !l.data) { setError(l.ok ? "El servidor no devolvió el Público." : l.error || textoError(l)); return; }
          setAlcance(l.data);
          if (l.data.publico?.segmentos?.length) setSegmentos(l.data.publico.segmentos);
        } catch (e) {
          if (mio === seq.current) setError(textoError(leerError(e)));
        } finally {
          if (mio === seq.current) setContando(false);
        }
      })();
    }, 250);
    return () => clearTimeout(t);
  }, [filtro, intento, claveCompras, claveExcluir]);

  // lo que dice la pantalla, para «Tu campaña»: mientras recuenta, sin el número de antes (era de otro filtro)
  const pueden = alcance && !contando && !error ? alcance.pueden : null;
  const archivo = lista ? lista.nombre : null;
  useEffect(() => {
    onContado?.({ pueden, segmentos, archivo });
  }, [onContado, pueden, segmentos, archivo]);

  const otros = alcance ? otrosExcluidos(alcance) : [];
  const sinConsentimiento = alcance ? textoSinConsentimiento(alcance) : "";
  const nadie = !!alcance && !contando && alcance.pueden === 0;
  // el camino al sí sólo si hay gente sin un sí: si el motivo es la fatiga o el celular, cargar contactos no cambia nada
  const faltaElSi = (Number(alcance?.excluidos?.sin_optin) || 0) > 0;

  return (
    <div className="space-y-4">
      <p className="text-sm text-slate-600">
        Las personas del Público con WhatsApp. Sale sólo quien dijo que sí a recibir mensajes de la marca: en los contactos con consentimiento o en la base de ventas.
      </p>
      {filtro.lista && (
        <div className="flex items-start justify-between gap-3 rounded-xl border border-violet-200 bg-violet-50/60 px-4 py-3 text-sm text-violet-900" data-testid="filtro-lista">
          <p className="flex items-start gap-2">
            <FileSpreadsheet size={16} className="mt-0.5 shrink-0" />
            <span>
              {lista
                ? <>Sólo los de tu archivo «{lista.nombreArchivo || lista.nombre}»: {num(lista.personasEnElPublico)} {plural(lista.personasEnElPublico, "persona", "personas")} de tu Público cuando lo subiste. Podés achicarlo con el segmento y la actividad.</>
                : errorLista ? <>No pudimos traer la lista del archivo. {errorLista}</> : "Sólo los de tu archivo…"}
            </span>
          </p>
          <button type="button" onClick={() => onFiltro(sinLista())}
            className="shrink-0 inline-flex items-center gap-1 text-xs font-semibold text-violet-700 hover:underline">
            <X size={13} /> Sacar el archivo
          </button>
        </div>
      )}
      <div className="grid gap-3 sm:grid-cols-2">
        <div>
          <label htmlFor={idSeg} className="text-xs font-semibold text-slate-600">Segmento</label>
          <select id={idSeg} value={filtro.segmento} onChange={(e) => onFiltro({ ...filtro, segmento: e.target.value })}
            className="mt-1 w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-violet-500">
            <option value="">Todos los segmentos</option>
            {segmentos.map((s) => (
              <option key={s.id} value={s.id}>{`${s.emoji ? `${s.emoji} ` : ""}${s.nombre} (${num(s.personas)})`}</option>
            ))}
          </select>
        </div>
        <div>
          <label htmlFor={idAct} className="text-xs font-semibold text-slate-600">Actividad</label>
          <select id={idAct} value={filtro.actividad} onChange={(e) => onFiltro({ ...filtro, actividad: e.target.value })}
            className="mt-1 w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-violet-500">
            <option value="">Cualquier actividad</option>
            {ACTIVIDADES_AUDIENCIA.map((a) => <option key={a} value={a}>{TEXTO_ACTIVIDAD[a]}</option>)}
          </select>
        </div>
      </div>

      {porCompras?.({
        resumen: claveCompras && !contando ? alcance?.compras ?? null : null,
        // también sin nada elegido: las listas que valen para todas las campañas las descuenta el backend solas
        exclusiones: !contando ? alcance?.exclusiones ?? null : null,
        contando,
      })}

      <div className="rounded-xl bg-slate-50 px-4 py-3 text-sm text-slate-700 space-y-2" aria-live="polite">
        {error ? (
          <p className="flex items-center justify-between gap-3 text-red-700" role="alert">
            <span>No pudimos contar el Público. {error}</span>
            <button type="button" onClick={() => setIntento((n) => n + 1)} className="font-semibold underline">Reintentar</button>
          </p>
        ) : !alcance || contando ? (
          <p className="flex items-center gap-2 text-slate-500"><Loader2 size={14} className="animate-spin" /> Contando…</p>
        ) : (
          <>
            {!alcance.publico.armadoAt && <p className="text-amber-800">El Público todavía no se armó: se arma solo cada 30 minutos.</p>}
            <p>
              <strong>{num(alcance.pueden)}</strong> de {num(alcance.personas)} {plural(alcance.personas, "persona", "personas")} con WhatsApp {plural(alcance.pueden, "puede", "pueden")} recibir por WhatsApp oficial.
            </p>
            {(sinConsentimiento || otros.length > 0 || alcance.bajas > 0) && (
              <ul className="list-disc pl-5 text-xs text-slate-600 space-y-0.5">
                {sinConsentimiento && <li>{sinConsentimiento}</li>}
                {otros.map((t) => <li key={t}>{t}</li>)}
                {alcance.bajas > 0 && <li>{num(alcance.bajas)} {plural(alcance.bajas, "pidió", "pidieron")} la baja de WhatsApp: no {plural(alcance.bajas, "está", "están")} en la lista.</li>}
              </ul>
            )}
            {nadie && (
              <div className="flex items-start gap-2 rounded-lg bg-amber-50 px-3 py-2 text-amber-900" role="status">
                <AlertTriangle size={15} className="mt-0.5 shrink-0" />
                <p>
                  Hoy nadie de este filtro puede recibir por WhatsApp oficial.
                  {faltaElSi && (
                    <>
                      {" "}Para mandarles,{" "}
                      <Link to={RUTA_DECLARAR_CONSENTIMIENTO} className="font-semibold underline">declará el consentimiento de la base de ventas</Link> o{" "}
                      <Link to={`${R_CARGAS}/nueva`} className="font-semibold underline">cargá los contactos con su consentimiento</Link>: aparecen acá en cuanto el Público se vuelva a armar.
                    </>
                  )}
                </p>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
